import {
  Building2,
  CalendarPlus,
  PauseCircle,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Badge,
  Card,
  Input,
  Button as ShButton,
  Skeleton,
} from '../../components/shadcn/index.js';
import {
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
} from '../../components/ui/index.js';
import { formatDate } from '../../lib/intl.js';
import { platformApi } from '../../lib/platformApi.js';
import { KpiCard } from '../reports/_shared/KpiCard.js';

import { TenantLogo } from './TenantLogo.js';

type TenantStatus = 'active' | 'suspended' | 'closed';
type TenantPlan = 'free' | 'starter' | 'pro' | 'enterprise';
type BillingStatus = 'trialing' | 'active' | 'past_due' | 'unpaid' | 'cancelled';

interface TenantListItem {
  tenant: {
    id: string;
    name: string;
    slug: string;
    status: TenantStatus;
    plan: TenantPlan;
    logoUrl: string | null;
    billingStatus: BillingStatus;
    currentPeriodEnd: string | null;
    trialEndsAt: string | null;
    monthlyPriceAmount: string | null;
    monthlyPriceCurrencyCode: string | null;
    createdAt: string;
  };
  userCount: number;
  planCount: number;
  domains: Array<{ hostname: string; kind: 'app' | 'storefront'; status: 'pending' | 'verified' | 'disabled'; isPrimary: boolean }>;
}

const STATUS_VARIANT: Record<TenantStatus, 'success' | 'warning' | 'secondary'> = {
  active: 'success',
  suspended: 'warning',
  closed: 'secondary',
};

// A 4px stripe on the start edge of the first cell — uses the KpiCard
// spine pattern (before-pseudo) so it doesn't shift column alignment.
const STATUS_ACCENT: Record<TenantStatus, string> = {
  active: '',
  suspended: 'before:absolute before:inset-y-0 before:start-0 before:w-1 before:bg-warning',
  closed: 'before:absolute before:inset-y-0 before:start-0 before:w-1 before:bg-fg-muted',
};

const STATUS_ROW: Record<TenantStatus, string> = {
  active: '',
  suspended: '',
  closed: 'opacity-60',
};

const PLAN_VARIANT: Record<TenantPlan, 'secondary' | 'info' | 'success' | 'default'> = {
  free: 'secondary',
  starter: 'info',
  pro: 'success',
  enterprise: 'default',
};

const BILLING_VARIANT: Record<BillingStatus, 'info' | 'success' | 'warning' | 'danger' | 'secondary'> = {
  trialing: 'info',
  active: 'success',
  past_due: 'warning',
  unpaid: 'danger',
  cancelled: 'secondary',
};

const PAGE_SIZE = 25;

export function PlatformTenantsPage(): JSX.Element {
  const { t, i18n } = useTranslation('platform');
  const [items, setItems] = useState<TenantListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [status, setStatus] = useState<'' | TenantStatus>('');
  const [planFilter, setPlanFilter] = useState<'' | TenantPlan>('');
  const [billingFilter, setBillingFilter] = useState<'' | BillingStatus>('');
  const [error, setError] = useState<string | null>(null);

  const hasActiveFilters =
    debouncedQ.trim().length > 0 || status !== '' || planFilter !== '' || billingFilter !== '';

  const clearFilters = (): void => {
    setQ('');
    setStatus('');
    setPlanFilter('');
    setBillingFilter('');
  };

  // Reset to the first page whenever the filters change.
  useEffect(() => {
    setOffset(0);
  }, [debouncedQ, status, planFilter, billingFilter]);

  useEffect(() => {
    const h = window.setTimeout(() => setDebouncedQ(q), 200);
    return () => window.clearTimeout(h);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params: Record<string, string> = {
          limit: String(PAGE_SIZE),
          offset: String(offset),
        };
        if (debouncedQ.trim()) params.q = debouncedQ.trim();
        if (status) params.status = status;
        if (planFilter) params.plan = planFilter;
        if (billingFilter) params.billingStatus = billingFilter;
        const r = await platformApi().get<{ items: TenantListItem[]; total: number }>(
          '/tenants',
          { params },
        );
        if (!cancelled) {
          setItems(r.data.items);
          setTotal(r.data.total);
        }
      } catch (e) {
        if (!cancelled) setError(String((e as Error).message ?? e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedQ, status, planFilter, billingFilter, offset]);

  const columns: DataTableColumn<TenantListItem>[] = [
    {
      id: 'name',
      header: t('tenants.table.name'),
      sortValue: (row) => row.tenant.name,
      cellClassName: 'relative font-medium',
      cell: (row) => (
        <div
          className={`flex items-center gap-2 ${STATUS_ACCENT[row.tenant.status]}`}
        >
          <TenantLogo
            logoUrl={row.tenant.logoUrl}
            imgClassName="h-6 w-6 rounded object-cover"
            fallback={
              <span className="flex h-6 w-6 items-center justify-center rounded bg-bg-subtle text-[0.65rem] font-semibold text-fg-muted">
                {row.tenant.name.charAt(0).toUpperCase()}
              </span>
            }
          />
          <Link
            to={`/platform/tenants/${row.tenant.id}`}
            className="hover:text-brand hover:underline underline-offset-2"
          >
            {row.tenant.name}
          </Link>
        </div>
      ),
    },
    {
      id: 'slug',
      header: t('tenants.table.slug'),
      sortValue: (row) => row.tenant.slug,
      cellClassName: 'font-mono text-xs text-fg-muted',
      cell: (row) => row.tenant.slug,
    },
    {
      id: 'domain',
      header: t('tenants.table.domain', { defaultValue: 'Domain' }),
      sortValue: (row) => row.domains[0]?.hostname ?? '',
      cell: (row) => {
        const d = row.domains[0];
        if (!d) return <span className="text-xs text-fg-muted">—</span>;
        const variant = d.status === 'verified' ? 'success' : d.status === 'pending' ? 'warning' : 'secondary';
        return (
          <div className="flex items-center gap-1.5">
            <a
              href={`https://${d.hostname}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="truncate text-xs hover:text-brand hover:underline"
            >
              {d.hostname}
            </a>
            <Badge variant={variant}>
              {t(`domains.status.${d.status}`, { defaultValue: d.status })}
            </Badge>
            {row.domains.length > 1 && (
              <span className="text-[0.7rem] text-fg-muted">+{row.domains.length - 1}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'status',
      header: t('tenants.table.status'),
      sortValue: (row) => t(`tenants.status.${row.tenant.status}`),
      cell: (row) => (
        <Badge variant={STATUS_VARIANT[row.tenant.status]}>
          {t(`tenants.status.${row.tenant.status}`)}
        </Badge>
      ),
    },
    {
      id: 'plan',
      header: t('tenants.table.plan'),
      sortValue: (row) => t(`tenants.plans.${row.tenant.plan}`),
      cell: (row) => (
        <Badge variant={PLAN_VARIANT[row.tenant.plan]}>
          {t(`tenants.plans.${row.tenant.plan}`)}
        </Badge>
      ),
    },
    {
      id: 'billing',
      header: t('tenants.table.billing'),
      sortValue: (row) => t(`tenants.billing.${row.tenant.billingStatus}`),
      cell: (row) => (
        <Badge variant={BILLING_VARIANT[row.tenant.billingStatus]}>
          {t(`tenants.billing.${row.tenant.billingStatus}`)}
        </Badge>
      ),
    },
    {
      id: 'expires',
      header: t('tenants.table.expires'),
      sortValue: (row) => row.tenant.currentPeriodEnd ?? row.tenant.trialEndsAt ?? '',
      cell: (row) => {
        const expiresAt = row.tenant.currentPeriodEnd ?? row.tenant.trialEndsAt;
        const expiresDate = expiresAt ? new Date(expiresAt) : null;
        const daysLeft = expiresDate
          ? Math.ceil((expiresDate.getTime() - Date.now()) / 86_400_000)
          : null;
        const expiresClass =
          daysLeft === null
            ? 'text-fg-muted'
            : daysLeft < 0
              ? 'text-danger font-medium'
              : daysLeft <= 7
                ? 'text-warning font-medium'
                : 'text-fg-muted';
        return (
          <span className={`text-xs ${expiresClass}`}>
            {expiresDate ? (
              <>
                {formatDate(expiresDate, { locale: i18n.language })}
                {daysLeft !== null && (
                  <span className="ms-1 text-[0.7rem] text-fg-muted">
                    {daysLeft < 0
                      ? t('tenants.expires.overdue', {
                          days: -daysLeft,
                          defaultValue: '({{days}}d overdue)',
                        })
                      : t('tenants.expires.inDays', {
                          days: daysLeft,
                          defaultValue: '({{days}}d left)',
                        })}
                  </span>
                )}
              </>
            ) : (
              '—'
            )}
          </span>
        );
      },
    },
    {
      id: 'users',
      header: t('tenants.table.users'),
      align: 'end',
      sortValue: (row) => Number(row.userCount),
      cellClassName: 'font-mono tabular-nums',
      cell: (row) => row.userCount,
    },
    {
      id: 'createdAt',
      header: t('tenants.table.createdAt'),
      sortValue: (row) => row.tenant.createdAt,
      cellClassName: 'text-xs text-fg-muted',
      cell: (row) => formatDate(row.tenant.createdAt, { locale: i18n.language }),
    },
  ];

  const stats = useMemo(() => {
    if (!items) return null;
    // Stats from the *current page* aren't representative of the whole
    // deployment when filters are active, so we keep the cards anchored
    // to the unfiltered totals via the response total + a small client-
    // side aggregation of the visible rows.
    const active = items.filter((r) => r.tenant.status === 'active').length;
    const suspended = items.filter((r) => r.tenant.status === 'suspended').length;
    const thisMonth = (() => {
      const now = new Date();
      const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      return items.filter((r) => new Date(r.tenant.createdAt) >= monthStart).length;
    })();
    return { active, suspended, thisMonth };
  }, [items]);

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('tenants.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('tenants.subtitle')}</p>
        </div>
        <Link to="/platform/tenants/new">
          <Button>{t('tenants.newButton')}</Button>
        </Link>
      </header>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          tone="brand"
          icon={Building2}
          label={t('tenants.kpi.total')}
          value={items ? String(total) : '—'}
          loading={!items}
        />
        <KpiCard
          tone="success"
          icon={ShieldCheck}
          label={t('tenants.kpi.active')}
          value={stats ? String(stats.active) : '—'}
          hint={stats && items ? t('tenants.kpi.onThisPage') : undefined}
          loading={!items}
        />
        <KpiCard
          tone="warning"
          icon={PauseCircle}
          label={t('tenants.kpi.suspended')}
          value={stats ? String(stats.suspended) : '—'}
          hint={stats && items ? t('tenants.kpi.onThisPage') : undefined}
          loading={!items}
        />
        <KpiCard
          tone="info"
          icon={CalendarPlus}
          label={t('tenants.kpi.newThisMonth')}
          value={stats ? String(stats.thisMonth) : '—'}
          hint={stats && items ? t('tenants.kpi.onThisPage') : undefined}
          loading={!items}
        />
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[18rem] flex-1">
            <Search
              className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted"
              aria-hidden
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('tenants.search')}
              aria-label={t('tenants.search')}
              className="ps-9"
            />
          </div>
          <select
            className="h-10 rounded-md border border-border bg-bg px-3 text-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
            aria-label={t('tenants.filter')}
          >
            <option value="">{t('tenants.all')}</option>
            <option value="active">{t('tenants.status.active')}</option>
            <option value="suspended">{t('tenants.status.suspended')}</option>
            <option value="closed">{t('tenants.status.closed')}</option>
          </select>
          <select
            className="h-10 rounded-md border border-border bg-bg px-3 text-sm"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value as typeof planFilter)}
            aria-label={t('tenants.table.plan')}
          >
            <option value="">{t('tenants.plans.all')}</option>
            <option value="free">{t('tenants.plans.free')}</option>
            <option value="starter">{t('tenants.plans.starter')}</option>
            <option value="pro">{t('tenants.plans.pro')}</option>
            <option value="enterprise">{t('tenants.plans.enterprise')}</option>
          </select>
          <select
            className="h-10 rounded-md border border-border bg-bg px-3 text-sm"
            value={billingFilter}
            onChange={(e) => setBillingFilter(e.target.value as typeof billingFilter)}
            aria-label={t('tenants.table.billing')}
          >
            <option value="">{t('tenants.billing.all')}</option>
            <option value="trialing">{t('tenants.billing.trialing')}</option>
            <option value="active">{t('tenants.billing.active')}</option>
            <option value="past_due">{t('tenants.billing.past_due')}</option>
            <option value="unpaid">{t('tenants.billing.unpaid')}</option>
            <option value="cancelled">{t('tenants.billing.cancelled')}</option>
          </select>
        </div>

        {error && (
          <p
            className="mt-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
            role="alert"
          >
            {error}
          </p>
        )}

        {items !== null && total > 0 && (
          <div className="mt-3 flex items-center justify-between text-xs text-fg-muted">
            <span>
              {t('tenants.pagination.showing', {
                from: offset + 1,
                to: Math.min(offset + items.length, total),
                total,
              })}
            </span>
            <div className="flex gap-2">
              <ShButton
                variant="outline"
                size="sm"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
              >
                {t('tenants.pagination.prev')}
              </ShButton>
              <ShButton
                variant="outline"
                size="sm"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + items.length >= total}
              >
                {t('tenants.pagination.next')}
              </ShButton>
            </div>
          </div>
        )}
      </Card>

      {items === null ? (
        <Card className="p-6"><Skeleton className="h-48 w-full" /></Card>
      ) : items.length === 0 ? (
        hasActiveFilters ? (
          <EmptyState
            title={t('tenants.noResults')}
            action={
              <ShButton variant="outline" size="sm" onClick={clearFilters}>
                {t('tenants.clearFilters')}
              </ShButton>
            }
          />
        ) : (
          <EmptyState title={t('tenants.empty')} />
        )
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={items}
            rowKey={(row) => row.tenant.id}
            rowClassName={(row) => STATUS_ROW[row.tenant.status] || undefined}
            paginated={false}
            searchable={false}
          />
        </Card>
      )}
    </div>
  );
}
